import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        message: formData.message,
        source: 'contact_form'
      });

      toast.success('Thank you! We\'ve received your inquiry and will contact you soon.');
      setIsSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        message: ''
      });

      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: 'Email',
      content: 'apply@elaraeducation.com',
      link: 'mailto:apply@elaraeducation.com'
    },
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      content: '+1 (647) 469-6738',
      link: 'tel:+16474696738'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Location',
      content: '1500 Meyerside Dr, Mississauga, ON L5T 1V4',
      link: null
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-primary text-white" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Get In Touch</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6" data-testid="contact-title">
              Let's Start Your Journey
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-300">
              Have questions about studying in Canada? Ready to apply? Our team is here to help you 
              every step of the way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-8 text-center border border-slate-200 hover:border-secondary transition-colors"
                data-testid={`contact-info-${index}`}
              >
                <div className="text-secondary mx-auto mb-4 inline-block">{info.icon}</div>
                <h3 className="text-lg font-semibold mb-2">{info.title}</h3>
                {info.link ? (
                  <a
                    href={info.link}
                    className="text-slate-600 hover:text-secondary transition-colors"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-slate-600">{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 md:py-32 bg-background-alt" data-testid="contact-form-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Form Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="form-info-title">
                Send Us a Message
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Fill out the form and one of our education consultants will get back to you within 24 hours.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-secondary mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Free Consultation</h3>
                    <p className="text-slate-600 text-sm">No obligation, no hidden fees</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-secondary mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Expert Guidance</h3>
                    <p className="text-slate-600 text-sm">Experienced education consultants</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-secondary mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Personalized Support</h3>
                    <p className="text-slate-600 text-sm">Tailored to your goals and needs</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {isSuccess ? (
                <div className="bg-green-50 border-l-4 border-green-500 p-8" data-testid="success-message">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="text-green-500" size={32} />
                    <h3 className="text-2xl font-semibold text-green-800">Thank You!</h3>
                  </div>
                  <p className="text-green-700">
                    We've received your inquiry and will contact you soon. Check your email for confirmation.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white p-8 shadow-sm" data-testid="contact-form">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-form-name"
                        className="w-full px-4 py-3 border border-slate-300 focus:outline-none focus:border-secondary transition-colors"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-form-email"
                        className="w-full px-4 py-3 border border-slate-300 focus:outline-none focus:border-secondary transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="contact-form-phone"
                        className="w-full px-4 py-3 border border-slate-300 focus:outline-none focus:border-secondary transition-colors"
                        placeholder="+1 (XXX) XXX-XXXX"
                      />
                    </div>

                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-slate-700 mb-2">
                        Current Location *
                      </label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        required
                        data-testid="contact-form-location"
                        className="w-full px-4 py-3 border border-slate-300 focus:outline-none focus:border-secondary transition-colors"
                        placeholder="City, Country"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                        Message / Query *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        data-testid="contact-form-message"
                        className="w-full px-4 py-3 border border-slate-300 focus:outline-none focus:border-secondary transition-colors resize-none"
                        placeholder="Tell us about your education goals..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-form-submit"
                      className="w-full px-8 py-4 bg-secondary text-primary font-semibold tracking-wide hover:bg-secondary-light transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send size={20} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="faq-title">
              Quick Answers
            </h2>
            <p className="text-slate-600 text-lg">
              Common questions we receive from prospective students
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                q: 'How long does the consultation process take?',
                a: 'Initial consultations typically last 30-60 minutes. We take the time to understand your goals and provide personalized recommendations.'
              },
              {
                q: 'What documents do I need for college applications?',
                a: 'Common documents include academic transcripts, English proficiency test scores (IELTS/TOEFL), passport copy, and statement of purpose. We\'ll provide a complete checklist based on your specific situation.'
              },
              {
                q: 'Do you charge for your services?',
                a: 'We offer free initial consultations. Our service fees are transparent and discussed upfront. Many of our services are covered by our partner colleges.'
              },
              {
                q: 'Can you help with study permit applications?',
                a: 'Yes, we guide you through the study permit process. However, for detailed immigration advice, we recommend consulting with a licensed immigration consultant.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-6 border-l-2 border-secondary"
                data-testid={`faq-item-${index}`}
              >
                <h3 className="font-semibold mb-2 text-lg">{faq.q}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};